import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

export default function FxSignalsPanel() {
  const [s, setS] = useState(null);
  const [recent, setRecent] = useState([]);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([api.get("/fx-signals/settings"), api.get("/fx-signals/recent")]);
      setS(a.data);
      setRecent(b.data);
      setForm({ expiry_min: a.data.expiry_min, max_drift_pips: a.data.max_drift_pips, max_per_day: a.data.max_per_day });
    } catch { /* not available */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const put = async (body, msg) => {
    try {
      const r = await api.put("/fx-signals/settings", body);
      setS(r.data);
      toast.success(msg);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  if (!s) return null;
  const ready = s.telegram_linked && s.oanda?.linked;
  const num = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="mt-6 border border-white/10 p-4" data-testid="fx-signals-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-xs">Telegram</div>
          <h2 className="font-heading text-xl font-bold">FX Signals — Approve or Reject</h2>
          <p className="text-sm text-zinc-400 mt-1">
            We scan the majors, let the AI pick the strongest buy/sell setups and send them to Telegram. Every trade carries a stop and target, with break-even protection. Nothing trades until you tap
            Approve, which places one real order on your linked OANDA account with stop-loss and take-profit.
          </p>
        </div>
        <Switch checked={!!s.enabled} disabled={!ready && !s.enabled}
          onCheckedChange={(v) => put({ enabled: v }, v ? "Signals on" : "Signals off")} />
      </div>
      {!s.telegram_configured && <p className="mt-2 text-sm text-amber-400">Telegram bot is not configured on the server yet.</p>}
      {s.telegram_configured && !s.telegram_linked && (
        <p className="mt-2 text-sm text-amber-400">Link Telegram first in <Link className="underline" to="/account">Account</Link>.</p>
      )}
      {!s.oanda?.linked && (
        <p className="mt-2 text-sm text-amber-400">Link your OANDA account first in <Link className="underline" to="/forex">Forex</Link>. Test on a practice account before going live.</p>
      )}
      <div className="mt-4 flex items-center gap-3 text-sm">
        <Switch checked={!!s.allow_live} onCheckedChange={(v) => put({ allow_live: v }, v ? "Live approvals allowed" : "Live approvals blocked")} />
        <span>Allow Approve to trade a <b>LIVE</b> OANDA account (off = practice only)</span>
      </div>
      <div className="mt-3 flex items-center gap-3 text-sm">
        <Switch checked={s.ai_filter !== false} onCheckedChange={(v) => put({ ai_filter: v }, v ? "AI filter on" : "AI filter off")} />
        <span>Skip signals the AI score disagrees with (e.g. strategy says BUY, AI says BEARISH)</span>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3 max-w-xl text-sm">
        <label>Expires (min)<Input type="number" value={form.expiry_min ?? ""} onChange={num("expiry_min")} /></label>
        <label>Max price drift (pips)<Input type="number" value={form.max_drift_pips ?? ""} onChange={num("max_drift_pips")} /></label>
        <label>Max signals / day<Input type="number" value={form.max_per_day ?? ""} onChange={num("max_per_day")} /></label>
      </div>
      <Button className="mt-3" onClick={() => put({
        expiry_min: Number(form.expiry_min), max_drift_pips: Number(form.max_drift_pips),
        max_per_day: Number(form.max_per_day) }, "Signal settings saved")}>Save</Button>
      {recent.length > 0 && (
        <table className="mt-4 w-full text-sm">
          <thead><tr className="text-left text-zinc-500"><th>Time</th><th>Pair</th><th>Side</th><th>Status</th></tr></thead>
          <tbody>
            {recent.map((r) => (
              <tr key={r.id} className="border-t border-white/5">
                <td>{String(r.created_at).slice(0, 16).replace("T", " ")}</td>
                <td>{r.pair}</td><td>{r.side}</td><td>{r.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
